import { HasPagination, PaginatorModel } from '@/app/models';
import { HttpParams } from '@angular/common/http';
import { Component, EventEmitter, inject, model, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Table, TableModule } from 'primeng/table';
import { Observable } from 'rxjs';
import { CheckboxComponent } from '../checkbox/checkbox.component';
import { TableModel } from './model/table.model';
import { downloadXlsx } from './tools/xlsx.tools';
import { PaginatorModule } from 'primeng/paginator';
import { FilterComponent } from './filter/filter.component';
import { TooltipModule } from 'primeng/tooltip';
import { InputSwitchModule } from 'primeng/inputswitch';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms'
import { SkeletonModule } from 'primeng/skeleton';
import { ButtonModule } from 'primeng/button';

@Component({
    selector: 'app-table',
    templateUrl: './table.component.html',
    styleUrl: './table.component.css',
    imports: [CommonModule, FormsModule, PaginatorModule, CheckboxComponent, FilterComponent, TableModule, TooltipModule, InputSwitchModule, SkeletonModule, ButtonModule],
    standalone: true
})
export class TableComponent<S = any, D = any> {
    /** @description El modelo para renderizar la tabla */
    tabla!: TableModel;
    /** @description El servicio de consulta a la base de datos */
    servicio!: any;
    /** @description La función para consultar, llave del servicio  */
    fx!: keyof S;

    private params = new HttpParams();

    private readonly route = inject(ActivatedRoute);

    async ngAfterViewInit() {
        // Separar filtros básicos y avanzados
        this.separarFiltros();

        if (this.tabla.queryParams) {
            this.route.queryParams.subscribe((data) => {
                if (!this.tabla?.filter) return;

                // 🔹 Filtrar solo los parámetros que están en `this.tabla.filter`
                const filtros = this.tabla.filter.filter((f) => Object.keys(data).includes(f.query));

                // 🔹 Actualizar los valores en `this.tabla.filter`
                filtros.forEach((filtro) => {
                    // Decodificar el valor del queryParam (viene encoded de la URL)
                    const rawValue = data[filtro.query];
                    const decodedValue = typeof rawValue === 'string' ? decodeURIComponent(rawValue) : rawValue;
                    
                    // Obtener el valor correcto basado en filtro.property
                    const valorEncontrado = filtro.searchProperty ? filtro.options?.find((o) => o[filtro.searchProperty!] == decodedValue) : decodedValue;

                    // Si `valorEncontrado` es un objeto, asignamos solo el valor relevante
                    filtro.value = filtro.searchProperty ? valorEncontrado || '' : (decodedValue ?? '');

                    // Asegurar que this.params.set() reciba un string
                    const valorParam = typeof filtro.value === 'object' ? filtro.value[filtro.searchProperty!] : filtro.value;

                    this.params = this.params.set(filtro.query, valorParam ?? '');
                });
            });
        }
        this.initParams();
    }
    initParams() {
        this.params = new HttpParams();
        this.params = this.params.set(this.tabla.page?.nombre ?? '', this.tabla.page?.valor ?? '');
        this.params = this.params.set(this.tabla.perPage?.nombre ?? '', this.tabla.perPage?.valor ?? '');
        this.params = this.params.set(this.tabla.sortBy?.nombre ?? '', this.tabla.sortBy?.valor ?? '');
        // Sincronizar cantidad de filas del paginator con el valor por defecto de perPage
        if (this.tabla.perPage?.valor) {
            const v = Number(this.tabla.perPage.valor as any);
            if (!Number.isNaN(v) && v > 0) this.rows = v;
        }
        this.getHistoric();
    }

    data: D[] = [];
    loading = false;

    totalRecords = 0;

    @ViewChild('table') private table!: Table;

    // Propiedades para filtros avanzados
    mostrarFiltrosAvanzados = false;
    filtrosBasicos: any[] = [];
    filtrosAvanzados: any[] = [];

    async getHistoric() {
        if (this.tabla.params) {
            this.tabla.params.forEach((p) => {
                this.params = this.params.set(p.nombre, p.valor);
            });
        }
        if (this.servicio ? typeof this.servicio[this.fx] === 'function' : false) {
            this.loading = true;
            // Llamar al método de servicio de manera dinámica
            (this.servicio[this.fx] as (params: HttpParams) => Observable<any>).call(this.servicio, this.params).subscribe({
                next: (res: any) => {
                    if (Array.isArray(res)) {
                        // Sin paginación del backend: aplicar filtros/paginación local como fallback
                        const filtered = this.applyLocalFilters(res as D[]);
                        this.totalRecords = filtered.length;
                        this.data = this.applyLocalPagination(filtered);
                    } else if (res && Array.isArray(res.data)) {
                        // Respuesta paginada esperada { data:[], metadata|meta:{ total|totalRecords, page, lastPage } }
                        this.data = res.data as D[];
                        const meta = res.metadata ?? res.meta ?? {};
                        const totalFromMeta =
                            (typeof meta.totalRecords === 'number' ? meta.totalRecords : undefined) ??
                            (typeof meta.total === 'number' ? meta.total : undefined) ??
                            (typeof meta.totalCount === 'number' ? meta.totalCount : undefined) ??
                            (typeof res.totalRecords === 'number' ? res.totalRecords : undefined) ??
                            (typeof res.total === 'number' ? res.total : undefined);
                        if (typeof totalFromMeta === 'number') this.totalRecords = totalFromMeta;
                        else if (typeof meta.lastPage === 'number' && this.rows) this.totalRecords = meta.lastPage * this.rows; // fallback aproximado
                        else this.totalRecords = this.data.length;
                        // Ajustar pagina actual (PrimeNG usa index base 0 en paginator 'first')
                        const pageFromMeta =
                            (typeof meta.page === 'number' ? meta.page : undefined) ??
                            (typeof meta.currentPage === 'number' ? meta.currentPage : undefined) ??
                            (typeof res.page === 'number' ? res.page : undefined) ??
                            (typeof res.currentPage === 'number' ? res.currentPage : undefined);
                        if (typeof pageFromMeta === 'number' && this.rows) {
                            const currentPage = pageFromMeta; // backend page base 1
                            this.first = (currentPage - 1) * this.rows;
                        }
                    } else if (res && Array.isArray(res.items)) {
                        // Variante común en endpoints: { items:[], page, limit, total } con opcional meta/metadata
                        this.data = res.items as D[];
                        const meta = res.metadata ?? res.meta ?? {};
                        const totalFromMeta =
                            (typeof meta.totalRecords === 'number' ? meta.totalRecords : undefined) ??
                            (typeof meta.total === 'number' ? meta.total : undefined) ??
                            (typeof meta.totalCount === 'number' ? meta.totalCount : undefined) ??
                            (typeof res.total === 'number' ? res.total : undefined);
                        this.totalRecords = typeof totalFromMeta === 'number' ? totalFromMeta : this.data.length;
                        const pageFromMeta =
                            (typeof meta.page === 'number' ? meta.page : undefined) ??
                            (typeof meta.currentPage === 'number' ? meta.currentPage : undefined) ??
                            (typeof res.page === 'number' ? res.page : undefined) ??
                            (typeof res.currentPage === 'number' ? res.currentPage : undefined);
                        if (typeof pageFromMeta === 'number' && this.rows) {
                            const currentPage = pageFromMeta; // backend page base 1
                            this.first = (currentPage - 1) * this.rows;
                        }
                    } else {
                        this.data = [];
                        this.totalRecords = 0;
                    }
                    this.loading = false;
                },
                error: (e) => {
                    this.loading = false;
                    console.error(e);
                }
            });
        } else {
            this.totalRecords = this.data.length;
        }
    }

    private applyLocalFilters(items: D[]): D[] {
        const filtros = this.tabla?.filter || [];
        if (!filtros.length) return items;

        let out = items;
        for (const filtro of filtros) {
            const raw = this.params.get(filtro.query);
            if (raw == null || raw === '') continue;

            const key = (filtro.property as string) || filtro.query;
            const rawLower = String(raw).toLowerCase();

            switch (filtro.type) {
                case 'input': {
                    out = out.filter((row: any) => {
                        const value = row?.[key];
                        if (value == null) return false;
                        return String(value).toLowerCase().includes(rawLower);
                    });
                    break;
                }
                case 'dropdown': {
                    out = out.filter((row: any) => String(row?.[key] ?? '') === String(raw));
                    break;
                }
                case 'multiselect': {
                    const set = raw.split(',').map((v) => v.trim()).filter(Boolean);
                    out = out.filter((row: any) => set.includes(String(row?.[key] ?? '')));
                    break;
                }
                case 'date': {
                    const target = this.normalizeDateKey(raw);
                    if (!target) break;
                    out = out.filter((row: any) => this.normalizeDateKey(row?.[key]) === target);
                    break;
                }
                default:
                    break;
            }
        }

        return out;
    }

    private applyLocalPagination(items: D[]): D[] {
        const pageName = this.tabla?.page?.nombre;
        const perPageName = this.tabla?.perPage?.nombre;

        const pageRaw = pageName ? this.params.get(pageName) : null;
        const perPageRaw = perPageName ? this.params.get(perPageName) : null;

        const page = Number(pageRaw ?? this.tabla?.page?.valor ?? 1);
        const perPage = Number(perPageRaw ?? this.tabla?.perPage?.valor ?? this.rows ?? items.length);

        const safePerPage = Number.isFinite(perPage) && perPage > 0 ? perPage : items.length;
        const safePage = Number.isFinite(page) && page > 0 ? page : 1;

        this.rows = safePerPage;
        this.first = (safePage - 1) * safePerPage;

        return items.slice(this.first, this.first + safePerPage);
    }

    private normalizeDateKey(value: any): string | null {
        if (value == null || value === '') return null;
        if (typeof value === 'string') {
            const m = value.match(/^(\d{4}-\d{2}-\d{2})/);
            if (m) return m[1];
        }
        const dt = value instanceof Date ? value : new Date(value);
        if (isNaN(dt.getTime())) return null;
        return dt.toISOString().slice(0, 10);
    }

    /**
     * Permite aplicar filtros externos (selects personalizados fuera del componente)
     * sin depender de FilterComponent. Reconstruye los params conservando paginado y orden.
     */
    applyExternalFilters(filters: { [k: string]: any }) {
        let newParams = new HttpParams();
        // mantener page / limit / sort
        if (this.tabla.page?.nombre) {
            const pageVal = this.params.get(this.tabla.page.nombre) || this.tabla.page.valor || 1;
            newParams = newParams.set(this.tabla.page.nombre, pageVal);
        }
        if (this.tabla.perPage?.nombre) {
            const perPageVal = this.params.get(this.tabla.perPage.nombre) || this.tabla.perPage.valor || 10;
            newParams = newParams.set(this.tabla.perPage.nombre, perPageVal);
        }
        if (this.tabla.sortBy?.nombre) {
            const sortVal = this.params.get(this.tabla.sortBy.nombre) || this.tabla.sortBy.valor || 'asc';
            newParams = newParams.set(this.tabla.sortBy.nombre, sortVal as string);
        }
        // agregar filtros (solo con valor definido y no vacío)
        Object.entries(filters).forEach(([k, v]) => {
            if (v === undefined || v === null) return;
            const value = typeof v === 'string' ? v.trim() : v;
            if (value === '' || (Array.isArray(value) && value.length === 0)) return;
            newParams = newParams.set(k, Array.isArray(value) ? value.join(',') : String(value));
        });
        this.params = newParams;
        // Reiniciar índice de la página en UI si existe paginator
        this.first = 0;
        this.getHistoric();
    }

    filter(ev: any) {
        if (ev.reset) {
            this.initParams();
            this.getHistoric();
            return;
        }
        const raw = ev?.value;
        const value = typeof raw === 'string' ? raw.trim() : raw;
        if (value === '' || value === undefined || value === null) {
            this.params = this.params.delete(ev.key);
        } else {
            this.params = this.params.set(ev.key, value);
        }
        if (this.tabla.page?.nombre) {
            this.params = this.params.set(this.tabla.page.nombre, 1);
            this.first = 0;
        }
        this.getHistoric();
    }

    first: number = 0;

    rows: number = 10;

    onPageChange(event: any) {
        this.first = event.first;
        this.rows = event.rows;
        this.params = this.params.set(this.tabla.page?.nombre ?? '', event.page + 1);
        this.params = this.params.set(this.tabla.perPage?.nombre ?? '', event.rows);
        this.getHistoric();
    }

    onSort(ev: any) {
        switch (ev.order) {
            case 1: {
                if (this.tabla.sortBy ? this.params.get(this.tabla.sortBy?.nombre) !== 'asc' : false) {
                    this.params = this.params.set(this.tabla.sortBy ? this.tabla.sortBy.nombre : '', 'asc');
                    this.getHistoric();
                }
                break;
            }
            case -1: {
                if (this.tabla.sortBy ? this.params.get(this.tabla.sortBy.nombre) !== 'desc' : false) {
                    this.params = this.params.set(this.tabla.sortBy ? this.tabla.sortBy.nombre : '', 'desc');
                    this.getHistoric();
                }
                break;
            }
        }
    }

    otherAction = false;

    onRowSelect(i: D) {
        if (!this.otherAction) {
            this.rowSelect.emit(i);
        }
        this.otherAction = false;
    }

    @Output() rowSelect = new EventEmitter<D>();

    //! Sección de selección de items -------------------------------------------->
    /** @description Los items seleccionados */
    selectedItems = model<D[]>([]);
    /** @description Marca si el check principal fue seleccionado */
    hasSelectedAll = false;
    /** @description Marca si el check individual fue seleccionado */
    selectedOne = false;
    /** @description La referencia del check principal */
    @ViewChild('principal') principal!: CheckboxComponent;
    checkItem(i: D, v: boolean) {
        //* Si todos fueron seleccionados, no debe actuar
        if (!this.hasSelectedAll) {
            //* Cambiamos el valor del check principal
            this.principal.valor.set(false);
            //* Obtenemos los valores actuales
            const data = this.selectedItems();
            let updatedItems: D[] = [];
            //* Pusheamos el item nuevo
            if (v) updatedItems = [...data, i];
            //! O lo sacamos
            else updatedItems = data.filter((item) => !this.isEqual(item, i));
            //* Update
            this.selectedItems.set(updatedItems);
            //* Volvemos al flujo normal
            this.selectedOne = false;
        }
    }
    /** @description Compara los items (objetos) de la lista */
    private isEqual(obj1: D, obj2: D) {
        return JSON.stringify(obj1) === JSON.stringify(obj2);
    }
    /** @description La referencia de todos los checklist */
    @ViewChildren('check') checks!: QueryList<CheckboxComponent>;
    checkAll(v: boolean) {
        //* Si se seleccionó uno, no debe actuar
        if (!this.selectedOne) {
            this.checks.forEach((c) => {
                //* Setea todos los check al mismo valor del principal
                c.valor.set(v);
            });
            //* Dependiendo de la selección, agrega todo o lo limpia
            this.selectedItems.set(v ? this.data : []);
            //* Volvemos al flujo normal
            this.hasSelectedAll = false;
        }
    }
    //! Sección de selección de iteems -------------------------------------------->

    returnData(i: any, property: string | number | symbol, subProperties: string[]): string | undefined {
        if (subProperties && subProperties.length > 0) {
            let data = '';
            subProperties.forEach((s) => {
                // Verifica que las propiedades existen antes de acceder a ellas
                if (i[property] && i[property][s] !== undefined) {
                    data += ' ' + i[property][s];
                }
            });
            return data ? data.trim() : undefined; // Devuelve el resultado sin espacios iniciales o finales
        } else {
            return i[property] !== undefined ? i[property] : undefined; // Verifica si existe la propiedad
        }
    }

    returnDataArray(i: any, property: string | number | symbol, subProperties: string[]): string {
        if (!i || !i[property] || !Array.isArray(i[property])) {
            return 'El objeto proporcionado no es un array';
        } else
            return i[property]
                .map((j: any) => {
                    if (subProperties ? subProperties.length === 0 : true) {
                        return typeof j == 'string' ? j.trim() : j;
                    }
                    let data = '';
                    subProperties.forEach((s) => {
                        if (j[s]) data += ' ' + j[s];
                    });
                    return data.trim();
                })
                .join(', ');
    }

    async generateXlsx(extraParams?: Record<string, any> | HttpParams) {
        if (this.servicio ? typeof this.servicio[this.fx] === 'function' : false) {
            let params = this.params ?? new HttpParams();

            if (this.tabla.params) {
                this.tabla.params.forEach((p) => {
                    params = params.set(p.nombre, p.valor);
                });
            }

            // Llamar al método de servicio de manera dinámica
            if (extraParams instanceof HttpParams) {
                extraParams.keys().forEach((key) => {
                    const values = extraParams.getAll(key) || [];
                    params = params.delete(key);
                    values.forEach((value) => {
                        params = params.append(key, value);
                    });
                });
            } else if (extraParams) {
                Object.entries(extraParams).forEach(([key, raw]) => {
                    params = params.delete(key);
                    if (raw === undefined || raw === null || raw === '') return;
                    if (Array.isArray(raw)) {
                        raw.forEach((value) => {
                            if (value !== undefined && value !== null && String(value).trim() !== '') {
                                params = params.append(key, String(value));
                            }
                        });
                    } else {
                        params = params.set(key, String(raw));
                    }
                });
            }

            this.servicio[this.fx].call(this.servicio, params).subscribe({
                next: async (data: any) => {
                    // Soportar múltiples formatos de respuesta
                    let rows: D[] = [];
                    if (data?.data && Array.isArray(data.data)) {
                        rows = data.data as D[];
                    } else if (Array.isArray(data?.items)) {
                        rows = data.items as D[];
                    } else if (Array.isArray(data)) {
                        rows = data as D[];
                    }
                    if (this.tabla.excel) await downloadXlsx(this.tabla.excel, rows);
                },
                error: (e: any) => {
                    console.error(e);
                }
            });
        }
    }

    /**
     * Método para alternar la visibilidad de los filtros avanzados
     */
    toggleFiltrosAvanzados() {
        this.mostrarFiltrosAvanzados = !this.mostrarFiltrosAvanzados;
    }

    /**
     * Método para separar filtros básicos y avanzados
     * Separa según la propiedad 'advanced' de cada filtro
     */
    private separarFiltros() {
        if (!this.tabla.filter) {
            this.filtrosBasicos = [];
            this.filtrosAvanzados = [];
            return;
        }

        // Separar filtros según la propiedad 'advanced'
        this.filtrosBasicos = this.tabla.filter.filter(filtro => !filtro.advanced);
        this.filtrosAvanzados = this.tabla.filter.filter(filtro => filtro.advanced === true);
    }

    /**
     * Helper para template: Angular intenta resolver 'Array' como propiedad del componente
     * al usar 'Array.isArray' directamente en plantillas standalone con control flow.
     * Exponemos un método para evitar el error NG9: Property 'Array' does not exist.
     */
    isArray(value: any): value is any[] {
        return Array.isArray(value);
    }
}
